import React, { useState } from "react";
import { Textarea, Button } from "@heroui/react";
import { Controller, UseFormReturn, FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

import internalNoteImg from "../../icon-internal_note.png";

interface InternalNoteProps {
  readonly methods: UseFormReturn<FieldValues>;
  readonly orderId: string;
  readonly status?: string;
}

export default function InternalNote({ methods, orderId, status }: InternalNoteProps) {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const [isSaving, setIsSaving] = useState(false);
  const isOrderSuccess = status === "success";

  const classNames = React.useMemo(
    () => ({
      inputWrapper: [
        "bg-[#FFFFFF] border-none",
        "group-data-[hover=true]:bg-[#FFFFFF]",
        "group-data-[focus=true]:bg-[#FFFFFF]",
      ],
    }),
    [],
  );

  const handleSaveInternalNote = async () => {
    setIsSaving(true);
    try {
      const internalNote = methods.getValues("internal_note");
      await axiosPrivate.put(
        `/orders/${orderId}`,
        {
          process: { internal_note: internalNote },
          save_internal_note: true
        }
      );
    } catch (error) {
      console.error("Error saving internal note:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="w-full flex">
      <div className="p-2 bg-[#9bbfc4] flex-grow rounded-l-large">
        <Controller
          name="internal_note"
          control={methods.control}
          render={({ field }) => (
            <Textarea
              {...field}
              placeholder={t("internal_note")}
              variant="flat"
              classNames={classNames}
              style={{ color: '#6c561d' }}
              isReadOnly={isOrderSuccess}
            />
          )}
        />
      </div>
      <div className="w-40 flex flex-col items-center justify-center p-3 bg-[#9bbfc4] rounded-r-large text-sm px-3">
        <div className="flex items-center mb-2">
          <img src={internalNoteImg} className="w-6 h-6 mr-1" alt={t("internal_note_icon")} />
          {t("internal_note")}
        </div>
        {!isOrderSuccess && (
          <Button
            size="sm"
            color="primary"
            onPress={handleSaveInternalNote}
            isLoading={isSaving}
            isDisabled={isSaving}
          >
            {t("save")}
          </Button>
        )}
      </div>
    </div>
  );
}
